import { Bell, Gift, LayoutDashboard, LogOut, Receipt, ShoppingBag, Store, UserCircle2 } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  Avatar,
  AvatarFallback,
  Button as ShButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Toaster,
  TooltipProvider,
} from '../components/shadcn/index.js';
import { useDirection, setLocale } from '../hooks/useDirection.js';
import { customerApi } from '../lib/customerApi.js';
import { useCustomerAuthStore } from '../lib/customerAuthStore.js';
import { setCurrencyDecimals } from '../lib/money.js';
import { useSiteContextStore } from '../lib/siteContextStore.js';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/portal/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/portal/orders', labelKey: 'nav.orders', icon: ShoppingBag },
  // Invoices and payments are TABS of the statement page now — one money
  // entry instead of three, and the crowded mobile bottom bar breathes again.
  { to: '/portal/statement', labelKey: 'nav.statement', icon: Receipt },
  { to: '/portal/cashback', labelKey: 'nav.cashback', icon: Gift },
  { to: '/portal/notifications', labelKey: 'nav.notifications', icon: Bell },
  { to: '/portal/profile', labelKey: 'nav.profile', icon: UserCircle2 },
];

export function CustomerPortalLayout({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation('portal');
  const { t: tc, i18n } = useTranslation();
  const dir = useDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const customer = useCustomerAuthStore((s) => s.customer);
  // The shop this portal belongs to. The portal is reachable straight from a
  // bookmark, so the slug can be absent for a moment (or entirely, on the bare app
  // host) — every "back to the shop" affordance below is therefore conditional
  // rather than rendering a link to /s/undefined.
  const storeSlug = useSiteContextStore((s) => s.tenant?.slug ?? null);
  const storeHref = storeSlug ? `/s/${storeSlug}` : null;
  // Gate the money-rendering children until the per-currency decimals fetch
  // settles, so IQD (→ 0) etc. never paints (and sticks) with the wrong decimals
  // on a direct portal load. Component state — NOT a module latch — so a failed
  // fetch proceeds with defaults yet retries on the next layout mount.
  const [decimalsReady, setDecimalsReady] = useState(false);
  const [unread, setUnread] = useState(0);

  // Unread-notification count for the Bell badge — polled, and refreshed on every
  // navigation (so it clears after the customer opens + reads the inbox).
  useEffect(() => {
    const load = (): void => {
      void customerApi()
        .get<{ unreadCount: number }>('/notifications', { params: { limit: 1 } })
        .then((r) => setUnread(r.data?.unreadCount ?? 0))
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [location.pathname]);

  // Register the backend per-currency decimals so portal money (IQD → 0 etc.)
  // formats correctly even on a direct portal load (no storefront visit first).
  useEffect(() => {
    void customerApi()
      .get<{ decimals: Record<string, number> }>('/currency-decimals')
      .then((r) => { if (r.data?.decimals) setCurrencyDecimals(r.data.decimals); })
      .catch(() => { /* non-fatal — falls back to CLDR; retried on next mount */ })
      .finally(() => setDecimalsReady(true));
  }, []);

  const onLogout = async (): Promise<void> => {
    try {
      await customerApi().post('/auth/logout', {});
    } finally {
      useCustomerAuthStore.getState().clear();
      navigate('/portal/login');
    }
  };

  const toggleLang = (): void => {
    void setLocale(i18n.language.startsWith('ar') ? 'en' : 'ar');
  };
  // Customers are phone-identified — the avatar shows the number's last two
  // digits (email is a legacy field that is null for phone accounts).
  const initials = customer?.phone
    ? customer.phone.replace(/\D/g, '').slice(-2) || 'C'
    : (customer?.email ?? 'C').slice(0, 2).toUpperCase();

  return (
    <TooltipProvider delayDuration={200}>
      <div className="relative isolate flex min-h-screen flex-col bg-bg-subtle text-fg" dir={dir}>
        <header className="sticky top-0 z-30 border-b border-brand/30 bg-bg/80 backdrop-blur supports-[backdrop-filter]:bg-bg/60">
          <div className="flex h-14 items-center justify-between gap-4 px-6">
            <div className="flex items-center gap-3">
              <span className="rounded-md bg-brand px-2 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-fg-inverse">
                {t('badge')}
              </span>
              <span className="hidden text-sm text-fg-muted md:inline">{t('subtitle')}</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Back to the shop. Same tab and a real <Link>, because this is a
                  RETURN, not a side trip — opening a second tab would leave the
                  customer with two of them and no way back in the one they had. */}
              {storeHref && (
                <ShButton asChild variant="outline" size="sm" className="h-8 gap-1.5 px-2.5 text-xs font-medium">
                  <Link to={storeHref}>
                    <Store className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">{t('backToStore', { defaultValue: 'Back to the shop' })}</span>
                  </Link>
                </ShButton>
              )}
              <ShButton variant="ghost" size="sm" onClick={toggleLang} aria-label={tc('actions.switchLanguage')} className="h-8 px-2 text-xs font-medium uppercase">
                {i18n.language.startsWith('ar') ? 'EN' : 'AR'}
              </ShButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full px-1 py-1 outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                    aria-label={tc('actions.userMenu')}
                  >
                    <Avatar className="h-8 w-8 bg-brand text-fg-inverse">
                      <AvatarFallback className="bg-brand text-fg-inverse">{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold">{t('badge')}</span>
                    <span className="text-xs font-normal text-fg-muted" dir="ltr">{customer?.phone ?? customer?.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate('/portal/profile')} className="gap-2">
                    <UserCircle2 className="h-4 w-4" aria-hidden />
                    {t('nav.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onLogout} className="gap-2 text-danger">
                    <LogOut className="h-4 w-4" aria-hidden />
                    {tc('actions.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div className="grid flex-1 grid-cols-1 lg:grid-cols-[15rem_1fr]">
          <aside
            className="hidden border-e border-border bg-bg lg:flex lg:flex-col"
            style={{ boxShadow: 'inset -2px 0 0 var(--color-brand)' }}
          >
            <div className="flex items-center gap-2 px-5 py-5">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand text-fg-inverse">
                <UserCircle2 className="h-4 w-4" aria-hidden />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('title')}</span>
            </div>
            <nav className="flex flex-col gap-0.5 px-3 pb-4" aria-label="Portal">
              {NAV_ITEMS.map((item) => {
                const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={[
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
                      active ? 'bg-brand/15 font-medium text-brand' : 'text-fg hover:bg-bg-subtle',
                    ].join(' ')}
                  >
                    <Icon className={`h-4 w-4 ${active ? 'text-brand' : 'text-fg-muted'}`} aria-hidden />
                    {t(item.labelKey)}
                    {item.to === '/portal/notifications' && unread > 0 && (
                      <span className="ms-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand px-1.5 text-[0.65rem] font-semibold text-brand-contrast">{unread > 99 ? '99+' : unread}</span>
                    )}
                  </Link>
                );
              })}
              {/* Separated from the portal's own pages, and last, because it LEAVES
                  the portal — it is a way out, not another tab of the same thing. */}
              {storeHref && (
                <Link
                  to={storeHref}
                  className="mt-2 flex items-center gap-3 rounded-md border-t border-border px-3 pb-2 pt-4 text-sm text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
                >
                  <Store className="h-4 w-4" aria-hidden />
                  {t('backToStore', { defaultValue: 'Back to the shop' })}
                </Link>
              )}
            </nav>
          </aside>

          <main className="px-4 py-6 sm:px-6 lg:px-10">
            {decimalsReady ? children : (
              <div className="grid min-h-[40vh] place-items-center text-fg-muted">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" aria-hidden />
              </div>
            )}
          </main>
        </div>

        {/* Mobile nav: simple horizontal scroller under the header */}
        <nav className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t border-border bg-bg/95 px-2 py-1.5 backdrop-blur lg:hidden" aria-label="Portal">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} aria-current={active ? 'page' : undefined} aria-label={t(item.labelKey)} className={`flex flex-col items-center gap-0.5 rounded-md px-2 py-1 text-[0.6rem] ${active ? 'text-brand' : 'text-fg-muted'}`}>
                <span className="relative">
                  <Icon className="h-5 w-5" aria-hidden />
                  {item.to === '/portal/notifications' && unread > 0 && (
                    <span className="absolute -end-1.5 -top-1 grid h-4 min-w-[1rem] place-items-center rounded-full bg-brand px-1 text-[0.55rem] font-semibold text-brand-contrast">{unread > 9 ? '9+' : unread}</span>
                  )}
                </span>
              </Link>
            );
          })}
        </nav>

        <Toaster position="top-right" richColors closeButton />
      </div>
    </TooltipProvider>
  );
}
